import { AlertTriangle, Coffee, CreditCard, UserCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { EmptyState } from "@/components/empty-state";
import { Metric } from "@/components/metric";
import { PageHeader } from "@/components/shell/PageHeader";
import { Pill, StatusDot, type Tone } from "@/components/status-dot";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { fullName, useDepartments } from "@/lib/data";
import { clock, dateTime, formatMinutes, longDate } from "@/lib/format";
import { AssignCardDialog } from "./AssignCardDialog";
import { useToday, useUnassignedEvents, type TodayRow, type UnassignedCard } from "./hooks";

const STATUS_TONE: Record<TodayRow["status"], Tone> = {
  present: "success",
  break: "warning",
  gone: "muted",
  not_started: "muted",
  absent: "vacation",
  day_off: "muted",
  holiday: "muted",
};

export function TodayPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenant, hasRole } = useAuth();
  const timezone = tenant!.timezone;
  const today = useToday();
  const departments = useDepartments();
  const unassigned = useUnassignedEvents({ enabled: hasRole("admin") });
  const [assigning, setAssigning] = useState<UnassignedCard | null>(null);

  const present = today.rows.filter((r) => r.status === "present").length;
  const breakDue = today.rows.filter((r) => r.breakDue).length;

  const grouped = useMemo(
    () =>
      (departments.data ?? []).map((d) => ({ department: d, rows: today.rows.filter((r) => r.employee.department_id === d.id) })).filter((g) => g.rows.length > 0),
    [departments.data, today.rows],
  );

  return (
    <>
      <PageHeader eyebrow={longDate(today.today)} title={t("today.title")} description={t("today.description")} />

      <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric accent label={t("today.present")} value={today.isLoading ? "–" : `${present}/${today.rows.length}`} icon={UserCheck} />
        <Metric label={t("today.missing_break")} value={today.isLoading ? "–" : breakDue} icon={Coffee} tone={breakDue > 0 ? "warning" : undefined} />
        <Metric
          label={t("today.anomalies")}
          value={today.isLoading ? "–" : today.openAnomalyDays}
          hint={t("today.anomalies_hint")}
          icon={AlertTriangle}
          tone={today.openAnomalyDays > 0 ? "warning" : undefined}
          onClick={() => navigate("/app/times?filter=anomalies")}
        />
        {hasRole("admin") && <Metric label={t("today.unassigned")} value={unassigned.data?.length ?? "–"} icon={CreditCard} />}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <section className="space-y-3">
          <h2 className="text-[15px] font-semibold">{t("today.list_title")}</h2>
          {today.isLoading && <Skeleton className="h-64 w-full rounded-xl" />}
          {grouped.map(({ department, rows }) => (
            <div key={department.id} className="overflow-hidden rounded-xl border bg-card">
              <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-2 text-[13px]">
                <span className="font-medium">{department.name}</span>
                <span className="tabular text-muted-foreground">
                  {rows.filter((r) => r.status === "present").length}/{rows.length}
                </span>
              </div>
              <ul className="divide-y">
                {rows.map((row) => (
                  <li key={row.employee.id}>
                    <button
                      onClick={() => navigate(`/app/times?employee=${row.employee.id}&day=${today.today}`)}
                      className="grid w-full grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-muted/40 sm:grid-cols-[minmax(0,1fr)_140px_90px_90px]"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{fullName(row.employee)}</span>
                        {row.result.anomalies.some((a) => a.severity === "warning") && (
                          <Pill tone="warning" className="mt-1">
                            {t(`anomaly.${row.result.anomalies.find((a) => a.severity === "warning")!.code}`)}
                          </Pill>
                        )}
                      </span>
                      <span className="flex items-center gap-2">
                        <StatusDot tone={STATUS_TONE[row.status]} label={row.absenceType ? t(`absence_type.${row.absenceType}`) : t(`status.${row.status}`)} />
                        {row.breakDue && <Coffee className="size-3.5 text-warning" aria-label={t("today.missing_break")} />}
                      </span>
                      <span className="tabular hidden text-[13px] text-muted-foreground sm:block">{row.firstIn ? clock(row.firstIn, timezone) : "–"}</span>
                      <span className="tabular hidden text-right text-[13px] sm:block">
                        {row.result.grossMinutes > 0 ? formatMinutes(row.result.grossMinutes) : "–"}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        {hasRole("admin") && (
          <section className="space-y-3">
            <div>
              <h2 className="text-[15px] font-semibold">{t("today.inbox_title")}</h2>
              <p className="text-[13px] text-muted-foreground">{t("today.inbox_hint")}</p>
            </div>
            {unassigned.data?.length === 0 && <EmptyState icon={CreditCard} title={t("today.inbox_empty")} />}
            <ul className="space-y-2">
              {unassigned.data?.map((card) => (
                <li key={card.actorId} className="flex items-center gap-3 rounded-xl border bg-card p-3">
                  <div className="flex size-9 items-center justify-center rounded-lg bg-brand-soft text-brand-ink">
                    <CreditCard className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium">{card.actorName ?? card.actorId}</div>
                    <div className="text-xs text-muted-foreground">
                      {t("today.taps", { count: card.count })} · {dateTime(card.lastAt, timezone)}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setAssigning(card)}>
                    {t("today.assign")}
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <AssignCardDialog card={assigning} onOpenChange={(open) => !open && setAssigning(null)} />
    </>
  );
}
